import * as THREE from 'three'
import Box from './Box'
import Frame from './Frame'
import { OneWayClog, TwoWayClog, NullClog, Direction } from './Clog'
import Size from './Size'

export default class ClogGenerator {
  public create(frame: Frame, box: Box): THREE.Mesh[] {
    console.log(box)
    if (box.clog instanceof NullClog) {
      return []
    }
    if (box.clog instanceof OneWayClog) {
      return this.createOneWayClogs(frame, box)
    }
    if (box.clog instanceof TwoWayClog) {
      return this.createTwoWayClogs(frame, box)
    }
    return []
  }

  private createOneWayClogs(frame: Frame, box: Box): THREE.Mesh[] {
    const param = <OneWayClog>box.clog
    const scale = frame.scale

    const num = param.getNum()
    if (num < 2) {
      return []
    }
    const clearance = param.getClearance()
    // NOTE 事前チェックでx >= z は確定している。
    const width = param.getWidth()
    const height = param.getHeight()
    const direction = param.getDirection()

    const clogs = []
    if (direction === Direction.W) {
      clogs.push(
        ...this.createXDirectionedOneWayClog(
          box.outer.x,
          box.outer.z,
          width,
          height,
          num,
          clearance,
          scale,
          frame.texture
        )
      )
    } else {
      clogs.push(
        ...this.createZDirectionedOneWayClog(
          box.outer.z,
          box.outer.x,
          width,
          height,
          num,
          clearance,
          scale,
          frame.texture
        )
      )
    }
    // 高さを調整
    for (let clog of clogs) {
      clog.translateY((box.outer.y / 2 + height / 2) * scale * -1)
    }
    return clogs
  }

  private createTwoWayClogs(frame: Frame, box: Box): THREE.Mesh[] {
    const clogs: THREE.Mesh[] = []
    const param = <TwoWayClog>box.clog
    const scale = frame.scale
    const width = param.getWidth()
    const height = param.getBlockHeight()
    const deckThick = param.getDeckThick()
    const xBlocks = param.getWidthBlocks()
    const zBlocks = param.getDepthBlocks()
    const direction = param.getDirection()

    const decks: THREE.Mesh[] = []
    const blocks: THREE.Mesh[] = []

    blocks.push(
      ...this.createBlocks(
        box.outer.x,
        box.outer.z,
        width,
        height,
        xBlocks,
        zBlocks,
        scale,
        frame.texture
      )
    )
    if (direction === Direction.W) {
      decks.push(
        ...this.createXDirectionedOneWayClog(
          box.outer.x,
          box.outer.z,
          width,
          deckThick,
          zBlocks,
          0,
          scale,
          frame.texture
        )
      )
    } else {
      decks.push(
        ...this.createZDirectionedOneWayClog(
          box.outer.z,
          box.outer.x,
          width,
          deckThick,
          xBlocks,
          0,
          scale,
          frame.texture
        )
      )
    }
    // ブロックの高さを調整
    for (let block of blocks) {
      block.translateY((box.outer.y / 2 + height / 2) * scale * -1)
      clogs.push(block)
    }
    // デッキの高さを調整
    for (let deck of decks) {
      deck.translateY((box.outer.y / 2 + height + deckThick / 2) * scale * -1)
      clogs.push(deck)
    }
    return clogs
  }
  private createXDirectionedOneWayClog(
    x: number,
    z: number,
    width: number,
    height: number,
    num: number,
    clearance: number,
    scale: number,
    texture: THREE.Texture
  ): THREE.Mesh[] {
    const clogs: THREE.Mesh[] = []
    const size = new Size(x * scale, height * scale, width * scale)
    // 開始位置はzのマイナス方向とする。
    // 全長から両端分の幅半分*２本分を引いて本数-1で割ると、均等割した距離が出る。
    // 最終的には開始位置に距離を足して、更に開始分の幅半分足す。
    const cz = z - clearance * 2
    const distance = (cz - (width / 2) * 2) / (num - 1)
    const start = (cz / 2) * -1
    for (let i = 0; i < num; i++) {
      const perDistance = distance * i
      const translate = (start + perDistance + width / 2) * scale
      clogs.push(this.createMesh(size, texture).translateZ(translate))
    }
    return clogs
  }

  private createZDirectionedOneWayClog(
    z: number,
    x: number,
    width: number,
    height: number,
    num: number,
    clearance: number,
    scale: number,
    texture: THREE.Texture
  ): THREE.Mesh[] {
    const clogs: THREE.Mesh[] = []
    const size = new Size(width * scale, height * scale, z * scale)
    const cx = x - clearance * 2
    const distance = (cx - (width / 2) * 2) / (num - 1)
    const start = (cx / 2) * -1
    for (let i = 0; i < num; i++) {
      const perDistance = distance * i
      const translate = (start + perDistance + width / 2) * scale
      clogs.push(this.createMesh(size, texture).translateX(translate))
    }
    return clogs
  }

  private createBlocks(
    x: number,
    z: number,
    width: number,
    height: number,
    xBlocksNum: number,
    zBlocksNum: number,
    scale: number,
    texture: THREE.Texture
  ): THREE.Mesh[] {
    const blocks: THREE.Mesh[] = []
    const size = new Size(width * scale, height * scale, width * scale)
    const xDistance = (x - (width / 2) * 2) / (xBlocksNum - 1)
    const zDistance = (z - (width / 2) * 2) / (zBlocksNum - 1)
    for (let i = 0; i < xBlocksNum; i++) {
      const xPerDistance = xDistance * i
      for (let j = 0; j < zBlocksNum; j++) {
        const zPerDistance = zDistance * j
        // console.log(
        //   `x=${(x / 2) * -1 + xPerDistance + width / 2} z=${(z / 2) * -1 +
        //     zPerDistance +
        //     width / 2}`
        // )
        const xTranslate = ((x / 2) * -1 + xPerDistance + width / 2) * scale
        const zTranslate = ((z / 2) * -1 + zPerDistance + width / 2) * scale
        blocks.push(
          this.createMesh(size, texture)
            .translateZ(zTranslate)
            .translateX(xTranslate)
        )
      }
    }
    return blocks
  }

  private createMesh(size: Size, texture: THREE.Texture): THREE.Mesh {
    const geometry = new THREE.CubeGeometry(size.x, size.y, size.z)
    const material = new THREE.MeshPhongMaterial({
      map: texture
    })
    const mesh = new THREE.Mesh(geometry, material)
    mesh.castShadow = true // 物体の影を有効にする。
    return mesh
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/Order/Components/Three/ClogGenerator.ts